/**
 * AI narration (Backlog 063): the voices creators may pick and the emotive tags they may write in
 * a script, grouped into palette categories. Admin only, like the Admin API's api/narration.
 *
 * Who may generate, and how much, is on the Plans page (AI narration + characters per month).
 */

import { useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, IconButton, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material'
import { Add, ArrowDownward, ArrowUpward, DeleteOutline, Edit } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { SearchField } from '@components/common/SearchField'
import { narrationApi } from '@api/endpoints/narration'
import type { AdminNarrationTagCategoryDto, AdminNarrationTagDto, AdminNarrationVoiceDto } from '@api/types/narration'
import { NarrationVoiceDialog } from '../components/narration/NarrationVoiceDialog'
import { NarrationTagDialog } from '../components/narration/NarrationTagDialog'
import { NarrationTagCategoryDialog } from '../components/narration/NarrationTagCategoryDialog'
import { PreviewButton } from '../components/narration/PreviewButton'
import { NarrationUsageTab } from '../components/narration/NarrationUsageTab'
import { usePreviewAudio } from '../components/narration/usePreviewAudio'
import {
  formatSetting,
  languageLabel,
  narrationErrorMessage,
  categoryName,
  categoryRequestFromDto,
  filterTagsByCategory,
  moveId,
  narrationTagCategoriesKey,
  narrationTagsKey,
  narrationVoicesKey,
  nextSortOrder,
  tagRequestFromDto,
  type TagCategoryFilter,
  voiceRequestFromDto,
} from '../components/narration/narrationForm'

// Tag categories are their own tab so the tags list is the first thing on "Emotive tags"
// rather than sitting under the categories table.
const TABS: ResponsiveTabItem[] = [
  { key: 'voices', label: 'Voices' },
  { key: 'tags', label: 'Emotive tags' },
  { key: 'categories', label: 'Tag categories' },
  { key: 'usage', label: 'Usage' },
]

export default function AdminNarrationPage() {
  const [tab, setTab] = useState('voices')
  const [voiceEditor, setVoiceEditor] = useState<{ voice: AdminNarrationVoiceDto | null } | null>(null)
  const [tagEditor, setTagEditor] = useState<{ tag: AdminNarrationTagDto | null } | null>(null)
  const [categoryEditor, setCategoryEditor] = useState<{ category: AdminNarrationTagCategoryDto | null } | null>(null)

  return (
    <PageShell
      title="Narration"
      subtitle="AI narration voices and emotive tags creators can use, and how much is generated. Who may generate, and how much, is set per plan on the Plans page."
      actions={
        tab === 'usage' ? undefined : tab === 'voices' ? (
          <Button variant="contained" startIcon={<Add />} onClick={() => setVoiceEditor({ voice: null })}>
            New voice
          </Button>
        ) : tab === 'categories' ? (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCategoryEditor({ category: null })}>
            New category
          </Button>
        ) : (
          <Button variant="contained" startIcon={<Add />} onClick={() => setTagEditor({ tag: null })}>
            New tag
          </Button>
        )
      }
    >
      <ResponsiveTabs tabs={TABS} value={tab} onChange={setTab} sx={{ mb: 2 }} />
      {tab === 'usage' ? (
        <NarrationUsageTab />
      ) : tab === 'voices' ? (
        <VoicesTab editor={voiceEditor} setEditor={setVoiceEditor} />
      ) : tab === 'categories' ? (
        <TagCategoriesTab editor={categoryEditor} setEditor={setCategoryEditor} />
      ) : (
        <TagsTab editor={tagEditor} setEditor={setTagEditor} />
      )}
    </PageShell>
  )
}

function VoicesTab({
  editor,
  setEditor,
}: {
  editor: { voice: AdminNarrationVoiceDto | null } | null
  setEditor: (e: { voice: AdminNarrationVoiceDto | null } | null) => void
}) {
  const queryClient = useQueryClient()
  const audio = usePreviewAudio()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [deleteTarget, setDeleteTarget] = useState<AdminNarrationVoiceDto | null>(null)

  const query = useQuery({ queryKey: narrationVoicesKey, queryFn: narrationApi.voices.list })
  const rows = useMemo(() => query.data ?? [], [query.data])

  const toggle = useMutation({
    mutationFn: (voice: AdminNarrationVoiceDto) =>
      narrationApi.voices.update(voice.id, { ...voiceRequestFromDto(voice), isActive: !voice.isActive }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: narrationVoicesKey }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => narrationApi.voices.remove(id),
    onSuccess: () => {
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: narrationVoicesKey })
    },
  })

  const columns = useMemo<DataTableColumn<AdminNarrationVoiceDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Voice',
        primary: true,
        render: (v) => (
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {v.displayName}
              </Typography>
              {!v.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
            </Stack>
            {v.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                {v.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'elevenLabs',
        header: 'ElevenLabs id',
        hideBelow: 'md',
        render: (v) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {v.elevenLabsVoiceId}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Stability {formatSetting(v.stability)} · Similarity {formatSetting(v.similarityBoost)} · Style{' '}
              {formatSetting(v.style)} · Speed {formatSetting(v.speed)}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'languages',
        header: 'Languages',
        render: (v) =>
          v.languages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Any
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {v.languages.map((code) => (
                <Chip key={code} size="small" variant="outlined" label={languageLabel(code)} />
              ))}
            </Stack>
          ),
      },
      {
        key: 'active',
        header: 'Active',
        align: 'center',
        render: (v) => (
          <Switch
            size="small"
            checked={v.isActive}
            disabled={toggle.isPending && toggle.variables?.id === v.id}
            inputProps={{ 'aria-label': `${v.isActive ? 'Deactivate' : 'Activate'} ${v.displayName}` }}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggle.mutate(v)}
          />
        ),
      },
      { key: 'sort', header: 'Sort', align: 'right', hideBelow: 'sm', render: (v) => v.sortOrder },
      {
        key: 'preview',
        header: 'Preview',
        align: 'center',
        render: (v) =>
          v.previewUrl ? (
            <PreviewButton
              url={v.previewUrl}
              playing={audio.playing}
              failed={audio.failed}
              onToggle={audio.toggle}
              label={v.displayName}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
    ],
    [audio.playing, audio.failed, audio.toggle, toggle],
  )

  return (
    <>
      {toggle.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => toggle.reset()}>
          {narrationErrorMessage(toggle.error, 'The voice could not be updated')}
        </Alert>
      )}

      <DataTable<AdminNarrationVoiceDto>
        rows={rows}
        columns={columns}
        getRowKey={(v) => v.id}
        loading={query.isLoading}
        error={query.error ? narrationErrorMessage(query.error, 'Failed to load voices') : null}
        emptyMessage="No voices yet. Until one is added, creators can't generate narration."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(voice) => setEditor({ voice })}
        getRowActions={(x) => [
          { key: 'edit', label: 'Edit', icon: <Edit fontSize="small" />, onClick: () => setEditor({ voice: x }) },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutline fontSize="small" />,
            color: 'error',
            onClick: () => {
              remove.reset()
              setDeleteTarget(x)
            },
          },
        ]}
      />

      {editor && (
        <NarrationVoiceDialog
          key={editor.voice?.id ?? 'new'}
          voice={editor.voice}
          existing={rows}
          nextSortOrder={nextSortOrder(rows)}
          onClose={() => setEditor(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete ${deleteTarget?.displayName ?? 'this voice'}?`}
        message={
          deleteTarget
            ? `"${deleteTarget.displayName}" is removed for good. Narration already generated keeps its audio, but saved scripts that name this voice need a new voice picked before they can be generated again. To hide it from creators instead, switch off Active.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      >
        {remove.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {narrationErrorMessage(remove.error, 'The voice could not be deleted')}
          </Alert>
        )}
      </ConfirmDialog>
    </>
  )
}

function TagsTab({
  editor,
  setEditor,
}: {
  editor: { tag: AdminNarrationTagDto | null } | null
  setEditor: (e: { tag: AdminNarrationTagDto | null } | null) => void
}) {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [deleteTarget, setDeleteTarget] = useState<AdminNarrationTagDto | null>(null)
  const [filter, setFilter] = useState<TagCategoryFilter>('all')
  const [search, setSearch] = useState('')

  const query = useQuery({ queryKey: narrationTagsKey, queryFn: narrationApi.tags.list })
  const categoriesQuery = useQuery({ queryKey: narrationTagCategoriesKey, queryFn: narrationApi.tagCategories.list })
  const allRows = useMemo(() => query.data ?? [], [query.data])
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const rows = useMemo(() => {
    const byCategory = filterTagsByCategory(allRows, filter)
    const q = search.toLowerCase()
    if (!q) return byCategory
    return byCategory.filter((t) => [t.tag, t.label, t.description].some((v) => v?.toLowerCase().includes(q)))
  }, [allRows, filter, search])
  // A filter naming a category that was just deleted falls back to every tag.
  const filterValue = filter === 'all' || filter === 'none' || categories.some((c) => c.id === filter) ? filter : 'all'

  const toggle = useMutation({
    mutationFn: (tag: AdminNarrationTagDto) =>
      narrationApi.tags.update(tag.id, { ...tagRequestFromDto(tag), isActive: !tag.isActive }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: narrationTagsKey }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => narrationApi.tags.remove(id),
    onSuccess: () => {
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: narrationTagsKey })
      queryClient.invalidateQueries({ queryKey: narrationTagCategoriesKey })
    },
  })

  const columns = useMemo<DataTableColumn<AdminNarrationTagDto>[]>(
    () => [
      {
        key: 'tag',
        header: 'Tag',
        primary: true,
        render: (t) => (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              [{t.tag}]
            </Typography>
            {!t.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
          </Stack>
        ),
      },
      { key: 'label', header: 'Label', render: (t) => t.label },
      {
        key: 'category',
        header: 'Category',
        render: (t) => {
          const c = categories.find((x) => x.id === t.categoryId)
          return (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <span>{categoryName(categories, t.categoryId)}</span>
              {c && !c.isActive && <Chip size="small" variant="outlined" label="Category off" />}
            </Stack>
          )
        },
      },
      {
        key: 'description',
        header: 'Description',
        hideBelow: 'md',
        render: (t) =>
          t.description ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {t.description}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        key: 'active',
        header: 'Active',
        align: 'center',
        render: (t) => (
          <Switch
            size="small"
            checked={t.isActive}
            disabled={toggle.isPending && toggle.variables?.id === t.id}
            inputProps={{ 'aria-label': `${t.isActive ? 'Deactivate' : 'Activate'} [${t.tag}]` }}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggle.mutate(t)}
          />
        ),
      },
      { key: 'sort', header: 'Sort', align: 'right', hideBelow: 'sm', render: (t) => t.sortOrder },
    ],
    [toggle, categories],
  )

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Creators write a tag in a script as [tag]. A bracketed word that isn't an active tag (or is in a switched-off
        category) is refused when they generate or preview; saved drafts keep it. The editors group tags by category,
        uncategorised ones under Other.
      </Typography>

      {toggle.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => toggle.reset()}>
          {narrationErrorMessage(toggle.error, 'The tag could not be updated')}
        </Alert>
      )}

      <DataTable<AdminNarrationTagDto>
        rows={rows}
        columns={columns}
        getRowKey={(t) => t.id}
        loading={query.isLoading}
        error={query.error ? narrationErrorMessage(query.error, 'Failed to load tags') : null}
        emptyMessage={
          search
            ? 'No tags match that search.'
            : filterValue === 'all'
              ? "No tags. Scripts can't use any [tag] until one is added."
              : 'No tags in this category.'
        }
        onRetry={() => query.refetch()}
        toolbar={
          <>
            <SearchField
              value={search}
              onSearch={(q) => {
                setSearch(q)
                setPage(0)
              }}
              placeholder="Search tags"
            />
            <TextField
              select
              size="small"
              label="Category"
              value={filterValue}
              onChange={(e) => {
                setFilter(e.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 1 220px' } }}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                  {c.isActive ? '' : ' (inactive)'}
                </MenuItem>
              ))}
              <MenuItem value="none">No category (Other)</MenuItem>
            </TextField>
          </>
        }
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(tag) => setEditor({ tag })}
        getRowActions={(x) => [
          { key: 'edit', label: 'Edit', icon: <Edit fontSize="small" />, onClick: () => setEditor({ tag: x }) },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutline fontSize="small" />,
            color: 'error',
            onClick: () => {
              remove.reset()
              setDeleteTarget(x)
            },
          },
        ]}
      />

      {editor && (
        <NarrationTagDialog
          key={editor.tag?.id ?? 'new'}
          tag={editor.tag}
          existing={allRows}
          categories={categories}
          defaultCategoryId={filterValue === 'all' || filterValue === 'none' ? '' : filterValue}
          nextSortOrder={nextSortOrder(allRows)}
          onClose={() => {
            setEditor(null)
            queryClient.invalidateQueries({ queryKey: narrationTagCategoriesKey })
          }}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget ? `Delete [${deleteTarget.tag}]?` : 'Delete this tag?'}
        message={
          deleteTarget
            ? `Scripts that use [${deleteTarget.tag}] will be refused the next time they are generated or previewed, until the tag is taken out of them. Switching off Active has the same effect on scripts but keeps the tag to switch back on.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      >
        {remove.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {narrationErrorMessage(remove.error, 'The tag could not be deleted')}
          </Alert>
        )}
      </ConfirmDialog>
    </>
  )
}

/** The palette's groups: list, create, edit, reorder (up/down), switch off and delete. */
function TagCategoriesTab({
  editor,
  setEditor,
}: {
  editor: { category: AdminNarrationTagCategoryDto | null } | null
  setEditor: (e: { category: AdminNarrationTagCategoryDto | null } | null) => void
}) {
  const queryClient = useQueryClient()
  const categoriesQuery = useQuery({ queryKey: narrationTagCategoriesKey, queryFn: narrationApi.tagCategories.list })
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const [page, setPage] = useState(0)
  // Large default page: reordering is up/down by one, so the whole palette should be on screen.
  // The move still works across a page boundary (it reorders the full list), it is just out of view.
  const [pageSize, setPageSize] = useState(100)
  const [deleteTarget, setDeleteTarget] = useState<AdminNarrationTagCategoryDto | null>(null)
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: narrationTagCategoriesKey })
    queryClient.invalidateQueries({ queryKey: narrationTagsKey })
  }

  const toggle = useMutation({
    mutationFn: (c: AdminNarrationTagCategoryDto) =>
      narrationApi.tagCategories.update(c.id, { ...categoryRequestFromDto(c), isActive: !c.isActive }),
    onSettled: refresh,
  })
  const reorder = useMutation({
    mutationFn: (ids: string[]) => narrationApi.tagCategories.reorder(ids),
    onSuccess: (list) => queryClient.setQueryData(narrationTagCategoriesKey, list),
    onSettled: refresh,
  })
  const remove = useMutation({
    mutationFn: (id: string) => narrationApi.tagCategories.remove(id),
    onSuccess: () => {
      setDeleteTarget(null)
      refresh()
    },
  })

  const ids = categories.map((c) => c.id)
  const move = (id: string, direction: -1 | 1) => reorder.mutate(moveId(ids, id, direction))

  const columns: DataTableColumn<AdminNarrationTagCategoryDto>[] = [
    {
      key: 'order',
      header: 'Order',
      width: 112,
      render: (c) => {
        const at = ids.indexOf(c.id)
        // Medium buttons (40px) rather than small: these are tapped repeatedly on a phone.
        return (
          <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            <IconButton aria-label={`Move ${c.name} up`} disabled={at <= 0 || reorder.isPending} onClick={() => move(c.id, -1)}>
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Move ${c.name} down`}
              disabled={at < 0 || at >= ids.length - 1 || reorder.isPending}
              onClick={() => move(c.id, 1)}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </Stack>
        )
      },
    },
    {
      key: 'name',
      header: 'Category',
      primary: true,
      render: (c) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {c.name}
          </Typography>
          {!c.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
        </Stack>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      hideBelow: 'md',
      render: (c) =>
        c.description ? (
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {c.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    { key: 'tags', header: 'Tags', align: 'right', render: (c) => c.tagCount },
    {
      key: 'active',
      header: 'Active',
      align: 'center',
      render: (c) => (
        <Switch
          size="small"
          checked={c.isActive}
          disabled={toggle.isPending && toggle.variables?.id === c.id}
          inputProps={{ 'aria-label': `${c.isActive ? 'Deactivate' : 'Activate'} ${c.name}` }}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle.mutate(c)}
        />
      ),
    },
  ]

  const failure = toggle.error ?? reorder.error
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The groups creators see in the tag palette, in this order. Use the arrows to move a category up or down.
      </Typography>
      {failure ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            toggle.reset()
            reorder.reset()
          }}
        >
          {narrationErrorMessage(failure, 'The category could not be updated')}
        </Alert>
      ) : null}
      <DataTable<AdminNarrationTagCategoryDto>
        rows={categories}
        columns={columns}
        getRowKey={(c) => c.id}
        loading={categoriesQuery.isLoading}
        error={categoriesQuery.error ? narrationErrorMessage(categoriesQuery.error, 'Failed to load categories') : null}
        onRetry={() => categoriesQuery.refetch()}
        emptyMessage="No categories. Every tag shows under Other."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(category) => setEditor({ category })}
        pageSizeOptions={[25, 50, 100]}
        getRowActions={(x) => [
          { key: 'edit', label: 'Edit', icon: <Edit fontSize="small" />, onClick: () => setEditor({ category: x }) },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutline fontSize="small" />,
            color: 'error',
            onClick: () => {
              remove.reset()
              setDeleteTarget(x)
            },
          },
        ]}
      />

      {editor && (
        <NarrationTagCategoryDialog
          key={editor.category?.id ?? 'new'}
          category={editor.category}
          existing={categories}
          nextSortOrder={(categories.reduce((max, c) => Math.max(max, c.sortOrder), 0) || 0) + 10}
          onClose={() => setEditor(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete this category?'}
        message={
          deleteTarget
            ? `Its ${deleteTarget.tagCount} tag${deleteTarget.tagCount === 1 ? '' : 's'} stay and move to Other (no category), still usable if active. Switching off Active instead hides them from creators and refuses them in scripts.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      >
        {remove.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {narrationErrorMessage(remove.error, 'The category could not be deleted')}
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  )
}
